import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, RefreshCw, Trophy, ChevronDown, ChevronUp,
  Crosshair, CircleDot, Hash, Shield, TrendingUp, Search, Zap,
  Star, AlertTriangle, CheckCircle2, Flame, Target, Brain,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';

const NumberBall = ({ number, size = 'md', glow = false, position = null, delay = 0 }) => {
  const sizeMap = {
    xl: 'w-16 h-16 text-2xl',
    lg: 'w-12 h-12 text-xl',
    md: 'w-10 h-10 text-base',
    sm: 'w-8 h-8 text-xs',
  };
  return (
    <div
      className="flex flex-col items-center gap-1"
      style={{ animation: `fadeSlideUp 0.5s ease-out ${delay}s both` }}
    >
      <div
        className={`${sizeMap[size]} rounded-full flex items-center justify-center font-black transition-all ${
          glow
            ? 'bg-gradient-to-br from-amber-400 via-yellow-500 to-orange-500 text-slate-950 shadow-lg shadow-amber-500/40 ring-2 ring-amber-400/50'
            : 'bg-slate-800 text-white border border-slate-700'
        }`}
        data-testid={`jugada-ball-${number}`}
      >
        {String(number).padStart(2, '0')}
      </div>
      {position !== null && (
        <span className="text-[9px] text-slate-500 font-medium">
          {['1ra', '2da', '3ra'][position]}
        </span>
      )}
    </div>
  );
};

const EngineBar = ({ name, value, color }) => {
  const barColor = value >= 70 ? 'bg-emerald-400' : value >= 50 ? 'bg-amber-400' : 'bg-slate-600';
  return (
    <div className="flex items-center gap-2">
      <span className={`text-[10px] w-14 truncate ${color}`}>{name}</span>
      <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} rounded-full transition-all duration-700`}
          style={{ width: `${Math.min(value, 100)}%` }}
        />
      </div>
      <span className="text-white text-[10px] font-bold w-8 text-right">{Math.round(value)}</span>
    </div>
  );
};

const ConfidenceMeter = ({ value }) => {
  const color = value >= 85 ? 'text-emerald-400' : value >= 70 ? 'text-amber-400' : 'text-red-400';
  const bgColor = value >= 85 ? 'from-emerald-500/20' : value >= 70 ? 'from-amber-500/20' : 'from-red-500/20';
  const strokeColor = value >= 85 ? '#34d399' : value >= 70 ? '#fbbf24' : '#f87171';
  const circumference = 2 * Math.PI * 40;
  const offset = circumference - (value / 100) * circumference;
  return (
    <div className={`relative w-24 h-24 bg-gradient-to-br ${bgColor} to-transparent rounded-full flex items-center justify-center`}>
      <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="40" fill="none" stroke="#1e293b" strokeWidth="6" />
        <circle
          cx="50" cy="50" r="40" fill="none"
          stroke={strokeColor} strokeWidth="6"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-1000"
        />
      </svg>
      <div className="text-center z-10">
        <span className={`text-xl font-black ${color}`}>{value}%</span>
      </div>
    </div>
  );
};

const ValidationBadges = ({ validation }) => {
  if (!validation) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {validation.boosts?.map((b, i) => (
        <span key={`boost-${i}-${b}`} className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-950/50 border border-emerald-800/40 rounded-full text-[10px] text-emerald-300">
          <CheckCircle2 size={10} /> {b}
        </span>
      ))}
      {validation.warnings?.map((w, i) => (
        <span key={`warn-${i}-${w}`} className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-950/50 border border-amber-800/40 rounded-full text-[10px] text-amber-300">
          <AlertTriangle size={10} /> {w}
        </span>
      ))}
    </div>
  );
};

const JugadaCard = ({ jugada, rank }) => {
  const [expanded, setExpanded] = useState(rank <= 2);

  const confColor = jugada.confidence >= 85 ? 'text-emerald-400 border-emerald-500/30'
    : jugada.confidence >= 70 ? 'text-amber-400 border-amber-500/30'
    : 'text-red-400 border-red-500/30';

  return (
    <div
      className={`rounded-xl overflow-hidden border transition-all ${
        rank === 1
          ? 'bg-gradient-to-br from-slate-900 via-amber-950/10 to-slate-900 border-amber-700/40 shadow-lg shadow-amber-900/10'
          : 'bg-slate-900/70 border-slate-800/50'
      }`}
      data-testid={`jugada-card-${jugada.lottery_id}`}
    >
      <button
        className="w-full p-4 flex items-center justify-between hover:bg-slate-800/20 transition-all"
        onClick={() => setExpanded(!expanded)}
        data-testid={`jugada-toggle-${jugada.lottery_id}`}
      >
        <div className="flex items-center gap-3">
          {rank <= 3 && (
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-sm ${
              rank === 1 ? 'bg-amber-500/20 text-amber-400' :
              rank === 2 ? 'bg-slate-400/20 text-slate-300' :
              'bg-orange-800/20 text-orange-400'
            }`}>
              #{rank}
            </div>
          )}
          <div className="text-left">
            <h3 className="text-white font-bold text-sm">{jugada.lottery_name}</h3>
            <div className="flex items-center gap-2 text-[10px] text-slate-500">
              <span>{jugada.draws_analyzed} sorteos</span>
              {jugada.draw_time && <span>{jugada.draw_time}</span>}
              {jugada.strategy && (
                <Badge variant="outline" className="text-[9px] py-0 border-slate-700 text-slate-500">
                  {jugada.strategy.replace('_', ' ')}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            {jugada.numbers.map((n, i) => (
              <NumberBall key={`h-${n}`} number={n} size="md" glow={rank <= 3} delay={i * 0.1} />
            ))}
          </div>
          <Badge variant="outline" className={`font-bold text-xs ${confColor}`}>
            {jugada.confidence}%
          </Badge>
          {expanded ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-800/50 space-y-4"
          style={{ animation: 'fadeSlideUp 0.3s ease-out' }}
        >
          {/* Confidence + Numbers */}
          <div className="flex items-start gap-6 mt-3">
            <ConfidenceMeter value={jugada.confidence} />
            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                <span>Ultimo sorteo:</span>
                <div className="flex gap-1">
                  {jugada.last_draw?.map((n, i) => (
                    <NumberBall key={`last-${n}`} number={n} size="sm" position={i} />
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 bg-slate-800/40 rounded-lg">
                  <span className="text-slate-500">Suma Jugada</span>
                  <p className="text-white font-bold">{jugada.jugada_sum}</p>
                </div>
                <div className="p-2 bg-slate-800/40 rounded-lg">
                  <span className="text-slate-500">Rango Historico</span>
                  <p className="text-white font-bold">
                    {jugada.validation?.sum_range?.q25}-{jugada.validation?.sum_range?.q75}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Validation */}
          <ValidationBadges validation={jugada.validation} />

          {/* Breakdown per number */}
          <div className="space-y-3">
            <h4 className="text-xs text-slate-400 font-bold flex items-center gap-1">
              <Brain size={12} /> Desglose por Motor
            </h4>
            {jugada.breakdown?.map((b, i) => (
              <div key={`bd-${b.number}`} className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/30">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <NumberBall number={b.number} size="sm" glow />
                    <div>
                      <span className="text-white font-bold text-sm">#{String(b.number).padStart(2, '0')}</span>
                      <span className="text-slate-500 text-[10px] ml-2">Terminal: {b.terminal}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`text-[10px] py-0 ${
                      parseInt(b.agreement) >= 4 ? 'border-emerald-500/40 text-emerald-400' :
                      parseInt(b.agreement) >= 3 ? 'border-amber-500/40 text-amber-400' :
                      'border-slate-600 text-slate-400'
                    }`}>
                      {b.agreement} motores
                    </Badge>
                    {b.momentum === 'rising' && (
                      <TrendingUp size={12} className="text-emerald-400" />
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <EngineBar name="Aries" value={b.engines.aries} color="text-purple-400" />
                  <EngineBar name="7IA" value={b.engines['7ia']} color="text-cyan-400" />
                  <EngineBar name="Power" value={b.engines.power} color="text-amber-400" />
                  <EngineBar name="Pale" value={b.engines.pale} color="text-blue-400" />
                  <EngineBar name="Tripleta" value={b.engines.tripleta} color="text-rose-400" />
                </div>
              </div>
            ))}
          </div>

          {/* Pales and Tripleta from jugada */}
          {jugada.pales?.length > 0 && (
            <div className="flex gap-4 text-xs">
              <div>
                <span className="text-slate-500 block mb-1">Pales sugeridos:</span>
                <div className="flex gap-2">
                  {jugada.pales.map((p, i) => (
                    <span key={`pale-${p[0]}-${p[1]}`} className="px-2 py-1 bg-cyan-950/40 border border-cyan-800/30 rounded text-cyan-300 font-bold">
                      {p[0]}-{p[1]}
                    </span>
                  ))}
                </div>
              </div>
              {jugada.tripleta?.length === 3 && (
                <div>
                  <span className="text-slate-500 block mb-1">Tripleta:</span>
                  <span className="px-2 py-1 bg-purple-950/40 border border-purple-800/30 rounded text-purple-300 font-bold">
                    {jugada.tripleta.join('-')}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Hot Terminals + Extended Candidates */}
          {(jugada.hot_terminals?.length > 0 || jugada.extended_candidates?.length > 0) && (
            <div className="space-y-3 mt-1">
              {jugada.hot_terminals?.length > 0 && (
                <div>
                  <span className="text-slate-500 text-xs block mb-1.5">
                    <Flame size={11} className="inline text-orange-400 mr-1" />
                    Terminales destacadas por el modelo:
                  </span>
                  <div className="flex gap-2">
                    {jugada.hot_terminals.map((t, i) => (
                      <span key={`ht-${t}`} className="w-8 h-8 rounded-lg bg-orange-950/50 border border-orange-700/40 flex items-center justify-center text-sm font-black text-orange-300">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {jugada.extended_candidates?.length > 0 && (
                <div>
                  <span className="text-slate-500 text-xs block mb-1.5">
                    <Target size={11} className="inline text-emerald-400 mr-1" />
                    Opciones alternativas por posicion:
                  </span>
                  <div className="grid grid-cols-3 gap-2">
                    {jugada.extended_candidates.map((cands, posIdx) => (
                      <div key={posIdx} className="p-2 bg-slate-800/30 rounded-lg border border-slate-700/20">
                        <span className="text-[9px] text-slate-500 block mb-1">{['1ra', '2da', '3ra'][posIdx]} posicion</span>
                        <div className="flex gap-1 flex-wrap">
                          {cands.map(([num, score], j) => (
                            <span key={j} className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                              j === 0 ? 'bg-emerald-900/40 border border-emerald-700/30 text-emerald-300'
                                : 'bg-slate-800/60 border border-slate-700/20 text-slate-400'
                            }`}>
                              {String(num).padStart(2, '0')}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function JugadaDelDia() {
  const { user, token } = useAuth();
  const [jugadas, setJugadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [date, setDate] = useState('');
  const [weights, setWeights] = useState(null);

  const fetchJugadas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/jugada/today`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJugadas(res.data.jugadas || []);
      setDate(res.data.date || '');
      setWeights(res.data.weights || null);
    } catch (e) {
      toast.error('Error cargando Jugada del Dia');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { if (token) fetchJugadas(); }, [token, fetchJugadas]);

  const filtered = search
    ? jugadas.filter(j => j.lottery_name.toLowerCase().includes(search.toLowerCase()))
    : jugadas;

  const topConf = jugadas.length > 0 ? Math.max(...jugadas.map(j => j.confidence)) : 0;
  const avgConf = jugadas.length > 0 ? Math.round(jugadas.reduce((a, j) => a + j.confidence, 0) / jugadas.length) : 0;

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
        <p className="text-slate-400">Inicia sesion para ver la Jugada del Dia</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e1a]" data-testid="jugada-del-dia-page">
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-6 pt-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" data-testid="back-btn">
                <ArrowLeft size={16} />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-black text-white flex items-center gap-2">
                <Trophy className="text-amber-400" size={24} />
                Jugada del Dia
              </h1>
              <p className="text-slate-500 text-sm">
                Consenso de 5 motores Sócrates — {date}
              </p>
            </div>
          </div>
          <Button
            variant="outline" size="sm" onClick={fetchJugadas} disabled={loading}
            className="border-slate-700 text-slate-400 hover:text-white"
            data-testid="refresh-btn"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        {/* Stats banner */}
        {!loading && jugadas.length > 0 && (
          <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-amber-950/30 via-slate-900/50 to-purple-950/30 border border-amber-900/30"
            style={{ animation: 'fadeSlideUp 0.4s ease-out' }}
          >
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-amber-400 text-xl font-black">{jugadas.length}</div>
                <div className="text-slate-500 text-xs">Loterias</div>
              </div>
              <div>
                <div className="text-emerald-400 text-xl font-black">{topConf}%</div>
                <div className="text-slate-500 text-xs">Puntaje maximo</div>
              </div>
              <div>
                <div className="text-cyan-400 text-xl font-black">{avgConf}%</div>
                <div className="text-slate-500 text-xs">Promedio</div>
              </div>
              <div>
                <div className="text-purple-400 text-xl font-black">5</div>
                <div className="text-slate-500 text-xs">Motores Sócrates</div>
              </div>
            </div>
          </div>
        )}

        {/* Info banner */}
        <div className="mb-4 p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
          <div className="flex items-start gap-2 text-xs text-slate-400">
            <Shield size={14} className="text-amber-400 mt-0.5 shrink-0" />
            <p>
              Cada jugada combina <strong className="text-amber-300">Aries</strong>,{' '}
              <strong className="text-cyan-300">7IA</strong>,{' '}
              <strong className="text-amber-300">Power Number</strong>,{' '}
              <strong className="text-blue-300">Pale</strong> y{' '}
              <strong className="text-rose-300">Tripleta</strong>.
              Los numeros con mayor convergencia entre motores reciben bonus.
              Se valida contra patrones historicos reales.
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="mb-4 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            placeholder="Buscar loteria..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-slate-900/70 border-slate-800 text-white pl-10"
            data-testid="search-lottery"
          />
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="animate-spin text-amber-400" size={36} />
            <p className="text-slate-500 text-sm">Calculando consenso de 5 motores...</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="jugada-list">
            {filtered.map((jugada, i) => (
              <JugadaCard key={jugada.lottery_id} jugada={jugada} rank={i + 1} />
            ))}
            {filtered.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                No se encontraron loterias
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
